import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminService from "../../../api/services/adminService";
import "../Home/Homepage.css";
import "./AllOpenOrdersPage.css";

const LIMIT = 50;

const FuturesOpenOrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [pairId, setPairId] = useState("");
  const [symbol, setSymbol] = useState("");
  const [side, setSide] = useState("");
  const [status, setStatus] = useState("");
  const [orderId, setOrderId] = useState("");
  const [reduceOnly, setReduceOnly] = useState("");
  const [closeOrder, setCloseOrder] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [debouncedUser, setDebouncedUser] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedUser(userSearch.trim()), 1000);
    return () => clearTimeout(t);
  }, [userSearch]);

  useEffect(() => {
    setPage(1);
  }, [pairId, symbol, side, status, orderId, reduceOnly, closeOrder, debouncedUser]);

  const buildParams = useCallback(() => {
    const params = { page, limit: LIMIT };
    if (pairId.trim()) params.pair_id = pairId.trim();
    if (symbol.trim()) params.symbol = symbol.trim();
    if (side) params.side = side;
    if (status === "all") params.status = "all";
    else if (status) params.status = status;
    if (orderId.trim()) params.order_id = orderId.trim();
    if (reduceOnly === "true" || reduceOnly === "false") params.reduce_only = reduceOnly;
    if (closeOrder === "true" || closeOrder === "false") params.close_order = closeOrder;
    if (debouncedUser) params.user = debouncedUser;
    return params;
  }, [page, pairId, symbol, side, status, orderId, reduceOnly, closeOrder, debouncedUser]);

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await AdminService.getGlobalFuturesOrders(buildParams());
        const list = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res)
            ? res
            : null;
        if (list) {
          setOrders(list);
          setTotalPages(res?.pagination?.total_pages || 1);
        } else {
          setOrders([]);
          setTotalPages(1);
          setError(res?.message || "Failed to load futures orders.");
        }
      } catch (e) {
        console.error(e);
        setOrders([]);
        setError("Failed to load futures orders.");
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [buildParams]);

  const formatDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  const formatNum = (n) =>
    n != null && !Number.isNaN(Number(n))
      ? Number(n).toLocaleString("en-IN", { maximumFractionDigits: 10 })
      : "—";

  const yn = (v) => (v === true ? "Yes" : v === false ? "No" : "—");

  const pairLabel = (row) =>
    row.pair?.name || row.pair?.short_name || row.symbol || "—";

  const userFullName = (u) => {
    if (!u) return "—";
    const first = u.first_name ?? u.firstName;
    const last = u.last_name ?? u.lastName;
    const parts = [first, last].filter(Boolean);
    return parts.length ? parts.join(" ") : "—";
  };

  return (
    <main className="dashboard-main dashboard-subpage">
      <section className="dashboard-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Futures Trading</span>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Futures Open Orders</span>
          </p>
          <h1 className="dashboard-hero__title">Futures Open Orders</h1>
          <p className="dashboard-hero__subtitle">
            Global futures orders (filters sent to API)
          </p>
        </div>
        <div className="dashboard-hero__icon">
          <i className="fas fa-chart-line" />
        </div>
      </section>

      <div className="dashboard-body">
        <section className="openorders-section">
          <div className="openorders-section__header">
            <div className="openorders-section__title-wrap">
              <span className="openorders-section__icon">
                <i className="fas fa-clipboard-list" />
              </span>
              <h2 className="openorders-section__title">Futures Orders</h2>
            </div>
            <div className="openorders-section__stats">
              <span className="openorders-stat">
                <strong>{orders.length}</strong> on page
              </span>
              {totalPages > 1 && (
                <span className="openorders-stat">
                  Page <strong>{page}</strong> / <strong>{totalPages}</strong>
                </span>
              )}
            </div>
          </div>

          {error && (
            <p style={{ color: "#dc2626", padding: "0 0.5rem 0.75rem", margin: 0 }}>{error}</p>
          )}

          <div
            className="openorders-search-wrap"
            style={{ flexWrap: "wrap", gap: "0.5rem", alignItems: "flex-end" }}
          >
            <input
              type="text"
              placeholder="User (email / UUID / phone) — 1s debounce"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              className="openorders-search-input"
              style={{ minWidth: 200, flex: "1 1 200px" }}
            />
            <input
              type="text"
              placeholder="Symbol (e.g. BTCUSDT)"
              value={symbol}
              onChange={(e) => setSymbol(e.target.value)}
              className="openorders-search-input"
              style={{ minWidth: 120, flex: "0 1 140px" }}
            />
            <input
              type="text"
              placeholder="Pair ID (ObjectId)"
              value={pairId}
              onChange={(e) => setPairId(e.target.value)}
              className="openorders-search-input"
              style={{ minWidth: 120, flex: "0 1 200px" }}
            />
            <input
              type="text"
              placeholder="Order ID (partial)"
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
              className="openorders-search-input"
              style={{ minWidth: 120, flex: "0 1 160px" }}
            />
            <select
              value={side}
              onChange={(e) => setSide(e.target.value)}
              className="openorders-search-input"
              style={{ flex: "0 1 140px" }}
            >
              <option value="">Side (any)</option>
              <option value="LONG">LONG</option>
              <option value="SHORT">SHORT</option>
            </select>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="openorders-search-input"
              style={{ flex: "0 1 200px" }}
            >
              <option value="">Status (open default)</option>
              <option value="all">all</option>
              <option value="PENDING">PENDING</option>
              <option value="WORKING">WORKING</option>
              <option value="PARTIAL">PARTIAL</option>
              <option value="FILLED">FILLED</option>
              <option value="CANCELLED">CANCELLED</option>
              <option value="REJECTED">REJECTED</option>
            </select>
            <select
              value={reduceOnly}
              onChange={(e) => setReduceOnly(e.target.value)}
              className="openorders-search-input"
              style={{ flex: "0 1 140px" }}
            >
              <option value="">Reduce only</option>
              <option value="true">true</option>
              <option value="false">false</option>
            </select>
            <select
              value={closeOrder}
              onChange={(e) => setCloseOrder(e.target.value)}
              className="openorders-search-input"
              style={{ flex: "0 1 140px" }}
            >
              <option value="">Close order</option>
              <option value="true">true</option>
              <option value="false">false</option>
            </select>
          </div>

          {loading ? (
            <div className="openorders-empty">
              <i className="fas fa-spinner fa-spin" /> Loading…
            </div>
          ) : orders.length > 0 ? (
            <>
              <div className="openorders-table-wrap">
                <table className="openorders-table">
                  <thead>
                    <tr>
                      <th>Order ID</th>
                      <th>UUID</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Pair</th>
                      <th>Side</th>
                      <th>Type</th>
                      <th>Lev</th>
                      <th>Qty</th>
                      <th>Price</th>
                      <th>Stop</th>
                      <th>Avg fill</th>
                      <th>Filled</th>
                      <th>Fee</th>
                      <th>SL</th>
                      <th>TP</th>
                      <th>R/O</th>
                      <th>Close</th>
                      <th>Status</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((row) => {
                      const docId = row.id ?? row._id;
                      const businessOrderId = row.orderId ?? row.order_id;
                      const uid = row.user?.id || row.user_id;
                      const u = row.user;
                      const uuidStr = u?.uuid ?? "—";
                      const nameStr = userFullName(u);
                      const emailStr = u?.email ?? "—";
                      const phoneStr = u?.phone ?? "—";
                      const p = pairLabel(row);
                      const qty = row.quantity ?? row.qty;
                      const filled = row.filledQty ?? row.filled ?? row.filled_quantity;
                      const fee = row.feePaid ?? row.fee ?? row.total_fee;
                      const avgFill =
                        row.avgFillPrice ?? row.avg_fill_price ?? row.avgFill;
                      const stopPx = row.stopPrice ?? row.stop_price;
                      const lev = row.leverage;
                      const reduceOnlyVal =
                        row.reduceOnly ?? row.reduce_only;
                      const closeOrderVal =
                        row.closeOrder ?? row.close_order;
                      const isSL = row.isSL ?? row.is_sl;
                      const isTP = row.isTP ?? row.is_tp;
                      const typeStr = (row.type ?? row.order_type ?? "").toUpperCase();
                      const priceDisplay =
                        row.price != null && row.price !== ""
                          ? formatNum(row.price)
                          : typeStr === "MARKET"
                            ? "Market"
                            : "—";
                      return (
                        <tr key={docId || businessOrderId}>
                          <td>
                            <span
                              className="openorders-order-id"
                              title={docId ? `Document id: ${docId}` : undefined}
                            >
                              {businessOrderId ?? docId ?? "—"}
                            </span>
                          </td>
                          <td>
                            {uid ? (
                              <Link
                                to={`/dashboard/trader/${uid}`}
                                state={{ trader: row.user }}
                                style={{ color: "#dc2626", fontWeight: 600, textDecoration: "none" }}
                              >
                                {uuidStr}
                              </Link>
                            ) : (
                              uuidStr
                            )}
                          </td>
                          <td>{nameStr}</td>
                          <td>{emailStr}</td>
                          <td>{phoneStr}</td>
                          <td>
                            <strong title={row.pair?.margin_asset ? `Margin: ${row.pair.margin_asset}` : ""}>
                              {p}
                            </strong>
                          </td>
                          <td>
                            <span className={`openorders-side openorders-side--${(row.side || "").toLowerCase()}`}>
                              {row.side ?? "—"}
                            </span>
                          </td>
                          <td>{row.type ?? row.order_type ?? "—"}</td>
                          <td>{lev != null ? `${lev}×` : "—"}</td>
                          <td>{formatNum(qty)}</td>
                          <td>{priceDisplay}</td>
                          <td>{stopPx != null && stopPx !== "" ? formatNum(stopPx) : "—"}</td>
                          <td>{formatNum(avgFill)}</td>
                          <td>{formatNum(filled)}</td>
                          <td>{formatNum(fee)}</td>
                          <td>{yn(isSL)}</td>
                          <td>{yn(isTP)}</td>
                          <td>{yn(reduceOnlyVal)}</td>
                          <td>{yn(closeOrderVal)}</td>
                          <td>{row.status ?? "—"}</td>
                          <td>{formatDate(row.createdAt)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && (
                <div style={{ padding: "0.75rem 0", display: "flex", gap: "0.5rem" }}>
                  <button
                    type="button"
                    className="cp-btn cp-btn--outline"
                    style={{ margin: 0 }}
                    disabled={page <= 1 || loading}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    className="cp-btn cp-btn--outline"
                    style={{ margin: 0 }}
                    disabled={page >= totalPages || loading}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          ) : (
            <p className="openorders-empty">No futures orders match your filters.</p>
          )}
        </section>
      </div>
    </main>
  );
};

export default FuturesOpenOrdersPage;
